import { minimatch } from 'minimatch'
import OpenAI, { ClientOptions } from 'openai'
import { zodResponseFormat } from 'openai/helpers/zod'
import { CompletionUsage } from 'openai/resources'
import { ChatCompletionChunk, ChatCompletionCreateParamsBase, ChatCompletionMessageFunctionToolCall, ChatCompletionMessageParam } from 'openai/resources/chat/completions'
import { Response, ResponseCreateParams, ResponseFunctionToolCall, ResponseInputItem, ResponseOutputMessage, ResponseStreamEvent, ResponseUsage, Tool, ToolChoiceFunction, ToolChoiceOptions } from 'openai/resources/responses/responses'
import LlmEngine from '../engine'
import logger from '../logger'
import Message from '../models/message'
import { ChatModel, EngineCreateOpts, ModelCapabilities, ModelMetadata, ModelOpenAI } from '../types/index'
import { LlmCompletionPayload, LLmContentPayloadImageOpenai, LlmChunk, LlmCompletionOpts, LlmContentPayload, LlmResponse, LlmRole, LlmStream, LlmStreamingContext, LlmStreamingResponse, LlmToolCall, LlmToolCallInfo, LlmToolChoice, LlmUsage } from '../types/llm'
import { PluginExecutionResult } from '../types/plugin'
import { toOpenAITools } from '../tools'
import { zeroUsage } from '../usage'
import { PROVIDER_BASE_URLS } from '../defaults'
import { RequestOptions } from 'openai/internal/request-options'

type OpenAIToolOpts = Omit<ChatCompletionCreateParamsBase, 'model' | 'messages' | 'stream'>

type ResponsesToolExecutionResult = {
  canceled?: boolean
  error?: unknown
}

type ResponsesSchemaConversion = {
  schema: any
  strict: boolean
}

const defaultBaseUrl = PROVIDER_BASE_URLS.openai!

function isChatCompletionReasoningEffort(effort: LlmCompletionOpts['reasoningEffort']): effort is 'minimal' | 'low' | 'medium' | 'high' {
  return effort === 'minimal' || effort === 'low' || effort === 'medium' || effort === 'high'
}

//
// https://platform.openai.com/docs/api-reference/introduction
// 

export type OpenAIStreamingContext = LlmStreamingContext<ChatCompletionMessageParam> & {
  reasoningContent: string
  textContent: string
  responsesApi: boolean
  thinking: boolean
  done?: boolean
}

export default class extends LlmEngine {

  client: OpenAI

  constructor(config: EngineCreateOpts, opts?: ClientOptions) {
    super(config)
    this.client = new OpenAI({
      apiKey: opts?.apiKey || config.apiKey,
      baseURL: opts?.baseURL || config.baseURL || defaultBaseUrl,
      timeout: opts?.timeout || config.timeout || undefined,
      dangerouslyAllowBrowser: true
    })
  }

  getId(): string {
    return 'openai'
  }

  // https://openai.com/api/pricing/

  getModelCapabilities(model: ModelMetadata): ModelCapabilities {

    const visionGlobs = [
      '*vision*',
      'gpt-4-turbo*',
      'gpt-4-0125*',
      'gpt-4-1106-vision-preview',
      'gpt-4o*',
      'chatgpt-4o',
      'gpt-4.1*',
      'gpt-4.5*',
      'gpt-5*',
      'o1*',
      'o3*',
      'o4*',
    ]

    const excludeVisionGlobs = [
      'gpt-4o-mini-audio*',
      'o1-mini*',
      'o3-mini*'
    ]

    const modelId = (model as ModelOpenAI).id

    return {
      tools: !modelId.includes('chat') && !modelId.startsWith('o1-mini'),
      vision: visionGlobs.some((m) => minimatch(modelId, m)) && !excludeVisionGlobs.some((m) => minimatch(modelId, m)),
      reasoning: modelId.startsWith('o') || modelId.startsWith('gpt-5'),
      caching: false,
    }
  }

  supportsServiceTiering(): boolean {
    return true
  }

  modelAcceptsSystemRole(model: string): boolean {
    return !model.startsWith('o1')
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  modelSupportsMaxTokens(model: ChatModel): boolean {
    return true
  }

  modelSupportsTemperature(model: ChatModel): boolean {
    return !model.capabilities.reasoning
  }

  modelSupportsTopP(model: ChatModel): boolean {
    return !model.capabilities.reasoning
  }

  modelSupportsTopK(model: ChatModel): boolean {
    return !model.capabilities.reasoning
  }

  modelSupportsReasoningEffort(model: ChatModel): boolean {
    return model.capabilities.reasoning
  }

  modelSupportsVerbosity(model: ChatModel): boolean {
    return model.id.startsWith('gpt-5')
  } 

  modelRequiresResponsesApi(model: ChatModel): boolean {
    return ['o3-pro*', 'codex*'].some((m) => minimatch(model.id, m))
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  modelSupportsStructuredOutput(model: ChatModel): boolean {
    return true
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  doesNotSendToolCallFinishReason(model: ChatModel): boolean {
    return false
  }

  get systemRole(): LlmRole {
    return 'system'//'developer'
  }

  async getModels(): Promise<ModelMetadata[]> {

    // // need an api key
    // if (!this.client.apiKey) {
    //   return []
    // }

    // do it
    try {

      const response = await this.client.models.list()
      let models = response.data
      if (models === null || models.length === 0) {
        // @ts-expect-error togetherai hack
        if (response.body && Array.isArray(response.body)) {
          // @ts-expect-error togetherai hack
          models = response.body as OpenAI.Model[]
        }
      }

      // done
      return models

    } catch (error) {
      console.error('Error listing models:', error);
      return []
    }
  }

  protected setBaseURL() {
    if (this.client) {
      this.client.baseURL = this.config.baseURL || defaultBaseUrl
    }
  }

  protected shouldUseResponsesApi(model: ChatModel, opts?: LlmCompletionOpts): boolean {
    return this.modelRequiresResponsesApi(model) || (opts?.useResponsesApi ?? false) || (this.config.useOpenAIResponsesApi ?? false) || (opts?.tools !== false && this.getInternalTools(this.getId(), opts).length > 0)
  }

  buildOpenAIPayload(model: ChatModel, thread: Message[] | string, opts?: LlmCompletionOpts): ChatCompletionMessageParam[] {

    let payloads = this.buildPayload<ChatCompletionMessageParam>(model, thread, opts)

    if (!this.modelAcceptsSystemRole(model.id)) {

      payloads = payloads.filter((msg) => msg.role !== 'system')

    } else if (this.systemRole !== 'system') {

      payloads = payloads.map((msg) => {
        if (msg.role === 'system') {
          (msg as any).role = this.systemRole
        }
        return msg
      })
    }

    return payloads
  }

  async chat(model: ChatModel, thread: ChatCompletionMessageParam[], opts?: LlmCompletionOpts): Promise<LlmResponse> {

    // process with responses api?
    if (this.shouldUseResponsesApi(model, opts)) {
      return this.responsesChat(model, thread as LlmCompletionPayload[], opts)
    }

    // Fallback to Chat Completions

    // set baseURL on client
    this.setBaseURL()

    // save tool calls
    const toolCallInfo: LlmToolCallInfo[] = []
    const startTime = Date.now()

    // call
    logger.log(`[${this.getName()}] prompting model ${model.id}`)
    const response = await this.client.chat.completions.create({
      model: model.id,
      messages: thread,
      ...this.getCompletionOpts(model, opts),
      ...await this.getToolsOpts(model, opts),
    }, this.getRequestOptions(model, opts));

    // get choice
    const choice = response.choices?.[0]

    // tool call
    if (choice.message.tool_calls && (choice?.finish_reason === 'tool_calls' || this.doesNotSendToolCallFinishReason(model))) {

      // add tool call message
      thread.push(choice.message)

      for (const tool_call of choice.message.tool_calls) {

        if (!('function' in tool_call)) {
          continue
        }

        // log
        const functionToolCall: ChatCompletionMessageFunctionToolCall = tool_call
        logger.log(`[openai] tool call ${functionToolCall.function.name} with ${functionToolCall.function.arguments}`)

        // this can error
        let args = null
        try {
          args = JSON.parse(functionToolCall.function.arguments)
        } catch (err) {
          throw new Error(`[openai] tool call ${functionToolCall.function.name} with invalid JSON args: "${functionToolCall.function.arguments}"`, { cause: err })
        }

        // now execute
        let lastUpdate: PluginExecutionResult|undefined = undefined
        for await (const update of this.callTool(
          { model: model.id, abortSignal: opts?.abortSignal },
          functionToolCall.function.name, args,
          opts?.toolExecutionDelegate,
          opts?.toolExecutionValidation,
        )) {
          if (update.type === 'result') {
            lastUpdate = update
          }
        }

        // process result
        const { content, canceled } = this.processToolExecutionResult(
          'openai',
          functionToolCall.function.name,
          args,
          lastUpdate
        )

        // For non-streaming, throw immediately on cancel
        if (canceled) {
          throw new Error('Tool execution was canceled')
        }

        // add tool response message
        thread.push({
          role: 'tool',
          tool_call_id: tool_call.id,
          content: JSON.stringify(content)
        })

        // save tool call info
        toolCallInfo.push({
          name: functionToolCall.function.name,
          params: args,
          result: content
        })

      }

      // apply cooldown before next request
      await this.applyCooldown(startTime)

      // prompt again
      const completion = await this.chat(model, thread, opts)

      // prepend tool call info
      completion.toolCalls = [
        ...toolCallInfo,
        ...completion.toolCalls ?? [],
      ]

      // cumulate usage
      if (opts?.usage && response.usage && completion.usage) {
        this.accumulateUsage(completion.usage, response.usage)
      }

      // done
      return completion

    }

    // total tokens is not part of our response
    if (response.usage?.total_tokens) {
      // @ts-expect-error "must be optional"???
      delete response.usage.total_tokens
    }

    // done
    return {
      type: 'text',
      content: choice.message.content || '',
      toolCalls: toolCallInfo,
      ...(opts?.usage && response.usage ? { usage: response.usage } : {}),
    }

  }

  async stream(model: ChatModel, thread: Message[], opts?: LlmCompletionOpts): Promise<LlmStreamingResponse<OpenAIStreamingContext>> {

    // process with responses api?
    if (this.shouldUseResponsesApi(model, opts)) {
      return this.responsesStream(model, thread, opts)
    }

    // set baseURL on client
    this.setBaseURL()

    // model: switch to vision if needed
    model = this.selectModel(model, thread, opts)
    const context: OpenAIStreamingContext = {
      model: model,
      responsesApi: false,
      reasoningContent: '',
      textContent: '',
      thread: this.buildOpenAIPayload(model, thread, opts),
      opts: opts || {},
      toolCalls: [],
      toolHistory: [],
      currentRound: 0,
      startTime: 0,
      usage: zeroUsage(),
      thinking: false,
      done: false,
    }

    // do it
    return {
      stream: await this.doStream(context),
      context: context,
    }

  }

  async doStream(context: OpenAIStreamingContext): Promise<LlmStream> {

    // reset
    context.toolCalls = []
    context.startTime = Date.now()
    context.textContent = ''

    // call
    logger.log(`[${this.getName()}] prompting model ${context.model.id}`)
    const stream = this.client.chat.completions.create({
      model: context.model.id,
      messages: context.thread,
      ...this.getCompletionOpts(context.model, context.opts),
      ...await this.getToolsOpts(context.model, context.opts),
      stream_options: { include_usage: context.opts.usage || false },
      stream: true,
    }, this.getRequestOptions(context.model, context.opts))
    
    // done
    return stream

  }

  getCompletionOpts(model: ChatModel, opts?: LlmCompletionOpts): Omit<ChatCompletionCreateParamsBase, 'model' | 'messages' | 'stream'> {
    const reasoningEffort = opts?.reasoningEffort
    return {
      ...(this.modelSupportsMaxTokens(model) && opts?.maxTokens ? { max_completion_tokens: opts?.maxTokens } : {}),
      ...(this.modelSupportsTemperature(model) && opts?.temperature ? { temperature: opts?.temperature } : {}),
      ...(this.modelSupportsTopK(model) && opts?.top_k ? { logprobs: true, top_logprobs: opts?.top_k } : {}),
      ...(this.modelSupportsTopP(model) && opts?.top_p ? { top_p: opts?.top_p } : {}),
      // The legacy Chat Completions endpoint accepts a narrower effort set than
      // the Responses API. Unsupported values remain available on Responses.
      ...(this.modelSupportsReasoningEffort(model) && isChatCompletionReasoningEffort(reasoningEffort)
        ? { reasoning_effort: reasoningEffort }
        : {}),
      ...(this.modelSupportsVerbosity(model) && opts?.verbosity ? { verbosity: opts.verbosity } : {}),
      ...(this.modelSupportsStructuredOutput(model) && opts?.structuredOutput ? {
          // @ts-expect-error structured output
          response_format: zodResponseFormat(opts.structuredOutput.structure, opts.structuredOutput.name)
        } : {}),
      ...(this.supportsServiceTiering() && opts?.serviceTier && { service_tier: opts.serviceTier }),
      ...(opts?.customOpts ? opts.customOpts : {}),
    }
  }

  getRequestOptions(model: ChatModel, opts?: LlmCompletionOpts): RequestOptions {
    return {
      ...(opts?.timeout ? { timeout: opts?.timeout } : {}),
    }
  }

  async getToolsOpts(model: ChatModel, opts?: LlmCompletionOpts): Promise<OpenAIToolOpts> {

    // check if enabled
    if (opts?.tools === false || !model.capabilities?.tools) {
      return {}
    }

    // tools - convert PluginTool[] to OpenAI format
    const toolDefs = await this.getAvailableTools(opts?.toolExecutionDelegate)
    const tools = toOpenAITools(toolDefs)
    if (!tools.length) return {}

    // default chat-completions style
    return {
      tools: tools as any,
      tool_choice: opts?.toolChoice?.type === 'tool' ? {
        type: 'function',
        function: { name: opts.toolChoice.name }
      } : opts?.toolChoice?.type ?? 'auto',
    }

  }

  async stop(stream: LlmStream) {
    stream?.controller?.abort()
  }

  syncToolHistoryToThread(context: OpenAIStreamingContext): void {
    // sync mutations from toolHistory back to thread
    // OpenAI thread format: { role: 'tool', tool_call_id, name, content }
    for (const entry of context.toolHistory) {
      const threadEntry = context.thread.find(
        (t: any) => t.role === 'tool' && t.tool_call_id === entry.id
      )
      if (threadEntry) {
        threadEntry.content = JSON.stringify(entry.result)
      }
    }
  }

  async *processNativeChunk(chunk: ChatCompletionChunk, context: OpenAIStreamingContext): AsyncGenerator<LlmChunk> {

    // response api events have already been translated to LLmChunk's
    if (context.responsesApi) {
      yield chunk as unknown as LlmChunk
      return
    }

    // Guard malformed chunks without choices
    if (!chunk?.choices || !Array.isArray(chunk.choices)) {
      return
    }

    // debug
    //console.dir(chunk, { depth: null })

    // cumulate usage
    if (chunk.usage && context.opts?.usage) {
      this.accumulateUsage(context.usage, chunk.usage)
    }

    // tool calls - normalize and process
    for (const tool_call of chunk.choices[0]?.delta?.tool_calls || []) {
      if (tool_call?.function) {
        const hasId = tool_call.id !== null && tool_call.id !== undefined && tool_call.id !== ''
        const existingToolCall = hasId ? context.toolCalls.find(tc => tc.id === tool_call.id) : null

        if (hasId && !existingToolCall) {
          // New tool call - normalize as 'start'
          yield* this.processToolCallChunk({
            type: 'start',
            id: tool_call.id,
            name: tool_call.function.name || '',
            args: tool_call.function.arguments || '',
            message: chunk.choices[0].delta.tool_calls!.map((tc: any) => {
              delete tc.index
              return tc
            }),
            metadata: {
              // @ts-expect-error reasoning details for some providers
              reasoningDetails: chunk.choices[0]?.delta?.reasoning_details,
            }
          }, context)
        } else {
          // Delta - append to existing tool call (by id if parallel, otherwise last)
          yield* this.processToolCallChunk({
            type: 'delta',
            id: hasId ? tool_call.id : undefined,
            argumentsDelta: tool_call.function.arguments || '',
          }, context)
        }
      }
    }

    // some providers (xai) do not send a finish_reason
    if (context.toolCalls.length && this.doesNotSendToolCallFinishReason(context.model)) {
      chunk.choices[0].finish_reason = chunk.choices[0].finish_reason || 'tool_calls'
    }

    // now tool calling
    if (['tool_calls', 'function_call', 'stop'].includes(chunk.choices[0]?.finish_reason || '') && context.toolCalls?.length) {

      // clear force tool call to avoid infinite loop
      if (context.opts.toolChoice?.type === 'tool') {
        delete context.opts.toolChoice
      }

      // increment round for next iteration
      context.currentRound++

      // execute tool calls using base class method
      yield* this.executeToolCallsSequentially(context.toolCalls, context, {
        formatToolCallForThread: (tc: LlmToolCall) => ({
          role: 'assistant' as const,
          content: context.textContent || '',
          ...(this.requiresReasoningContent() ? { reasoning_content: context.reasoningContent } : {}),
          ...(tc.reasoningDetails ? { reasoning_details: tc.reasoningDetails } : {}),
          tool_calls: tc.message
        }),
        formatToolResultForThread: (result: any, tc: LlmToolCall) => ({
          role: 'tool' as const,
          tool_call_id: tc.id,
          name: tc.function,
          content: JSON.stringify(result),
        }),
        createNewStream: async () => this.doStream(context)
      })

      // done
      return

    }

    // done?
    const done = ['stop', 'length', 'content_filter', 'eos'].includes(chunk.choices?.[0]?.finish_reason || '')
    if (done) {
      context.done = true
    }

    // @ts-expect-error reasoning content for some providers
    if (chunk.choices?.length && chunk.choices?.[0]?.delta?.reasoning_content) {
      // @ts-expect-error reasoning content for some providers
      context.reasoningContent += chunk.choices?.[0]?.delta?.reasoning_content
      yield {
        type: 'reasoning',
        // @ts-expect-error reasoning content for some providers
        text: chunk.choices?.[0]?.delta?.reasoning_content || '',
        done: done,
      }
    }

    // text chunk
    if (chunk.choices?.length && (chunk.choices?.[0]?.delta?.content || done)) {
      const text = chunk.choices?.[0]?.delta?.content || ''
      if (!context.thinking) {
        context.textContent += text
      }
      yield {
        type: context.thinking ? 'reasoning' : 'content',
        text: text,
        done: done
      }
    }

    // usage
    if (context.opts?.usage && context.done && context.usage) {
      yield { type: 'usage', usage: context.usage }
    }

  }

  defaultRequiresFlatTextPayload(model: ChatModel, msg: Message): boolean {
    return super.requiresFlatTextPayload(model, msg)
  }

  requiresFlatTextPayload(model: ChatModel, msg: Message): boolean {
    return this.defaultRequiresFlatTextPayload(model, msg) || (this.client.baseURL?.length > 0 && this.client.baseURL !== defaultBaseUrl)
  }

  accumulateUsage(cumulate: LlmUsage, usage: CompletionUsage) {

    cumulate.prompt_tokens += usage.prompt_tokens ?? 0
    cumulate.completion_tokens += usage.completion_tokens ?? 0
    
    if (usage.prompt_tokens_details?.cached_tokens) {
      cumulate.prompt_tokens_details!.cached_tokens! += usage.prompt_tokens_details?.cached_tokens
    }
    if (usage.prompt_tokens_details?.audio_tokens) {
      cumulate.prompt_tokens_details!.audio_tokens! += usage.prompt_tokens_details?.audio_tokens
    }
    
    if (usage.completion_tokens_details?.reasoning_tokens) {
      cumulate.completion_tokens_details!.reasoning_tokens! += usage.completion_tokens_details?.reasoning_tokens
    }
    if (usage.completion_tokens_details?.audio_tokens) {
      cumulate.completion_tokens_details!.audio_tokens! += usage.completion_tokens_details?.audio_tokens
    }    
  }

  //
  // Response API stuff
  //

  // ---------------------------------------------------------------------------
  // Responses API – via official SDK with automatic tool execution
  async responsesChat(model: ChatModel, thread: LlmCompletionPayload[], opts?: LlmCompletionOpts): Promise<LlmResponse> {

    // log
    logger.log(`[${this.getName()}] prompting model ${model.id}`)

    // Build request for Responses API
    const request = await this.buildResponsesRequest(model, thread, opts, false)

    // init stuff
    let text = ''
    const toolCallInfo: LlmToolCallInfo[] = []
    const usage: LlmUsage = zeroUsage()

    // debug
    logger.debug('[responses] REQUEST', JSON.stringify(request, null, 2))

    // call
    let response: Response = await this.client.responses.create(request) as Response

    // we can loop several times calling tools
    while (true) {

      // update responseId tracking
      logger.debug('[responses] RESPONSE', response)

      // cumulate usage
      if (opts?.usage && response.usage) {
        this.accumulateResponsesUsage(usage, response.usage)
      }

      // concatenate text from the output array
      const messages = response.output.filter((o: any) => o.type === 'message') as ResponseOutputMessage[]
      for (const message of messages) {
        for (const content of message.content) {
          if (content.type === 'output_text') {
            text += content.text || ''
          }
        }
      }
      
      // check if we have tool calls
      const toolCalls = response.output?.filter((o: any) => o.type === 'function_call') as ResponseFunctionToolCall[]
      if (toolCalls.length) {

        const followReqInput: any[] = []
        for (const toolCall of toolCalls) {

          // log
          logger.log(`[openai] tool call ${toolCall.name} with ${toolCall.arguments}`)

          // this can error
          let args = null
          try {
            args = JSON.parse(toolCall.arguments)
          } catch (err) {
            throw new Error(`[openai] tool call ${toolCall.name} with invalid JSON args: "${toolCall.arguments}"`, { cause: err })
          }

          // now execute
          let lastUpdate: PluginExecutionResult|undefined = undefined
          for await (const update of this.callTool(
            { model: model.id, abortSignal: opts?.abortSignal },
            toolCall.name, args,
            opts?.toolExecutionDelegate,
            opts?.toolExecutionValidation,
          )) {
            if (update.type === 'result') {
              lastUpdate = update
            }
          }

          // process result
          const { content, canceled } = this.processToolExecutionResult(
            'openai',
            toolCall.name,
            args,
            lastUpdate
          )

          // canceled - if validation denied, we should not continue the loop
          if (canceled) {
            // For responses API, we need to send the error back as the output
            followReqInput.push({
              type: 'function_call_output',
              call_id: toolCall.call_id,
              output: JSON.stringify({ error: 'Tool execution was canceled' }),
            })
            // save tool call info with canceled status
            toolCallInfo.push({
              name: toolCall.name,
              params: args,
              result: { error: 'Tool execution was canceled' }
            })
            // stop processing more tools
            break
          }

          // store
          followReqInput.push({
            type: 'function_call_output',
            call_id: toolCall.call_id,
            output: typeof content === 'string' ? content : JSON.stringify(content),
          })

          // save tool call info
          toolCallInfo.push({
            name: toolCall.name,
            params: args,
            result: content
          })
        
        }

        // build follow-up request
        const followUpReq: ResponseCreateParams = {
          model: model.id,
          previous_response_id: response.id,
          input: followReqInput,
          stream: false,
        }
        await this.attachResponsesTools(followUpReq, model, opts)
        
        // debug
        logger.debug('[responses] FOLLOW-UP REQUEST', JSON.stringify(followUpReq, null, 2))

        // continue
        response = await this.client.responses.create(followUpReq)
        continue
      }

      // done
      return {
        type: 'text',
        content: text,
        toolCalls: toolCallInfo,
        openAIResponseId: response?.id,
        ...(opts?.usage ? { usage: usage } : {}),
      }
    }
  }

  async responsesStream(model: ChatModel, thread: Message[], opts?: LlmCompletionOpts): Promise<LlmStreamingResponse<OpenAIStreamingContext>> {

    // log
    logger.log(`[${this.getName()}] prompting model ${model.id}`)

    const request = await this.buildResponsesRequestFromMessages(model, thread, opts, true)
    const stream = await this.client.responses.create(request) as AsyncIterable<ResponseStreamEvent>
    logger.debug('[responsesStream] subscribed')

    const context: OpenAIStreamingContext = {
      model,
      opts: opts || {},
      usage: zeroUsage(),
      thread: [],
      toolCalls: [],
      toolHistory: [],
      currentRound: 0,
      startTime: Date.now(),
      reasoningContent: '',
      textContent: '',
      responsesApi: true,
      thinking: false,
    }

    // async generator bound to preserve class context
    async function* generator(this: any) {
      
      // track the response id
      let responseId: string = ''

      // we need to accumulate usage
      const usage: LlmUsage = context.usage

      // We may need to run multiple streaming passes if the model calls tools.
      let currentStream: AsyncIterable<ResponseStreamEvent> | null = stream
      while (true) {

        // Track function-call tool invocations that the model initiates while streaming.
        // We gather their incremental arguments and execute them once finalized.
        const pendingCalls: ResponseFunctionToolCall[] = []
        const toolExecutions = new Map<string, Promise<ResponsesToolExecutionResult>>()
        const toolChunkQueue: LlmChunk[] = []
        const preparationStatuses: Record<string, string> = {}
        let thinking = false
        let toolRoundStarted = false
        let wakeToolChunks: (() => void) | null = null
        let toolChunkWakePromise: Promise<void> | null = null

        const getToolCallKey = (toolCall: ResponseFunctionToolCall, fallbackIndex?: number): string => {
          return toolCall.id || toolCall.call_id || `${toolCall.name}:${fallbackIndex ?? pendingCalls.indexOf(toolCall)}`
        }

        const enqueueToolChunk = (chunk: LlmChunk) => {
          toolChunkQueue.push(chunk)
          if (wakeToolChunks) {
            wakeToolChunks()
            wakeToolChunks = null
            toolChunkWakePromise = null
          }
        }

        const ensureToolRoundStarted = () => {
          if (!toolRoundStarted) {
            context.currentRound++
            context.toolCalls = []
            context.startTime = Date.now()
            toolRoundStarted = true
          }
        }

        function* drainToolChunks() {
          while (toolChunkQueue.length) {
            yield toolChunkQueue.shift()!
          }
        }

        const waitForToolChunks = (): Promise<void> => {
          if (toolChunkQueue.length) {
            return Promise.resolve()
          }
          if (!toolChunkWakePromise) {
            toolChunkWakePromise = new Promise(resolve => {
              wakeToolChunks = resolve
            })
          }
          return toolChunkWakePromise
        }

        const findPendingCall = (item: any): ResponseFunctionToolCall | undefined => {
          return pendingCalls.find(call => {
            return (item.id && call.id === item.id) || (item.call_id && call.call_id === item.call_id)
          })
        }

        const mergeToolCall = (target: ResponseFunctionToolCall, source: any) => {
          if (source.name) target.name = source.name
          if (source.call_id) target.call_id = source.call_id
          if (typeof source.arguments === 'string' && source.arguments.length) {
            target.arguments = source.arguments
          }
        }

        const parseToolCallArgs = (toolCall: ResponseFunctionToolCall): any => {
          const rawArgs = toolCall.arguments || ''
          try {
            return rawArgs.trim() ? JSON.parse(rawArgs) : {}
          } catch (err) {
            throw new Error(`[openai] tool call ${toolCall.name} with invalid JSON args: "${rawArgs}"`, { cause: err })
          }
        }

        const startToolExecution = (toolCall: ResponseFunctionToolCall) => {
          const key = getToolCallKey(toolCall)
          if (toolExecutions.has(key)) {
            return
          }
          ensureToolRoundStarted()

          const args = parseToolCallArgs(toolCall)
          const toolCallId = toolCall.call_id || toolCall.id || key
          context.toolCalls.push({
            id: toolCallId,
            function: toolCall.name,
            args,
            message: toolCall,
          })

          enqueueToolChunk({
            type: 'tool',
            id: toolCall.id,
            name: toolCall.name,
            state: 'running',
            status: this.getToolRunningDescription(toolCall.name, args),
            call: {
              params: args,
              result: undefined
            },
            done: false
          } as LlmChunk)

          const execution = (async (): Promise<ResponsesToolExecutionResult> => {
            try {
              let lastUpdate: PluginExecutionResult|undefined = undefined
              for await (const update of this.callTool({ model: model.id, abortSignal: opts?.abortSignal }, toolCall.name, args, opts?.toolExecutionDelegate, opts?.toolExecutionValidation)) {

                if (opts?.abortSignal?.aborted) {
                  enqueueToolChunk({
                    type: 'tool',
                    id: toolCall.id,
                    name: toolCall.name,
                    state: 'canceled',
                    status: this.getToolCanceledDescription(toolCall.name, args) || 'Tool execution was canceled',
                    done: true,
                    call: {
                      params: args,
                      result: undefined
                    }
                  } as LlmChunk)
                  return { canceled: true }
                }

                if (update.type === 'status') {
                  enqueueToolChunk({
                    type: 'tool',
                    id: toolCall.id,
                    name: toolCall.name,
                    state: 'running',
                    status: update.status,
                    call: {
                      params: args,
                      result: undefined
                    },
                    done: false
                  } as LlmChunk)

                } else if (update.type === 'result') {
                  lastUpdate = update
                }

              }

              const { content, canceled: toolCallCanceled } = this.processToolExecutionResult(
                'openai',
                toolCall.name,
                args,
                lastUpdate
              )

              enqueueToolChunk({
                type: 'tool',
                id: toolCall.id,
                name: toolCall.name,
                state: toolCallCanceled ? 'canceled' : 'completed',
                status: toolCallCanceled
                  ? this.getToolCanceledDescription(toolCall.name, args) || (typeof content === 'object' ? content.error : undefined) || 'Tool execution was canceled'
                  : this.getToolCompletedDescription(toolCall.name, args, content),
                done: true,
                call: {
                  params: args,
                  result: content
                }
              } as LlmChunk)

              context.toolHistory.push({
                id: toolCallId,
                name: toolCall.name,
                args,
                result: content,
                round: context.currentRound,
              })

              if (toolCallCanceled) {
                return { canceled: true }
              }

              return {}

            } catch (error) {
              if (opts?.abortSignal?.aborted) {
                enqueueToolChunk({
                  type: 'tool',
                  id: toolCall.id,
                  name: toolCall.name,
                  state: 'canceled',
                  status: this.getToolCanceledDescription(toolCall.name, args),
                  done: true,
                  call: {
                    params: args,
                    result: undefined
                  }
                } as LlmChunk)
                return { canceled: true }
              }
              return { error }
            }
          })()

          toolExecutions.set(key, execution)
        }

        async function* waitForToolExecution(execution: Promise<ResponsesToolExecutionResult>): AsyncGenerator<LlmChunk, ResponsesToolExecutionResult> {
          const executionDone = execution.then(result => ({ type: 'result' as const, result }))

          while (true) {
            if (toolChunkQueue.length) {
              yield* drainToolChunks()
              continue
            }

            const next = await Promise.race([
              executionDone,
              waitForToolChunks().then(() => ({ type: 'chunks' as const }))
            ])

            if (next.type === 'chunks') {
              yield* drainToolChunks()
              continue
            }

            yield* drainToolChunks()
            return next.result
          }
        }

        for await (const ev of currentStream!) {

          switch (ev.type) {

            case 'response.created':
              responseId = ev.response.id
              break

            case 'response.in_progress':
              yield {
                type: 'openai_message_id',
                id: ev.response.id,
              }
              break

            case 'response.completed': {
              if (opts?.usage && ev.response.usage) {
                this.accumulateResponsesUsage(usage, ev.response.usage)
              }
              break
            }

            case 'response.output_item.added':
              switch (ev.item.type) {
                
                case 'message':
                  thinking = false
                  break
                
                case 'reasoning':
                  thinking = true
                  break
                
                case 'function_call': {

                  // record the tool call
                  pendingCalls.push(ev.item)
                  const partialArgs = this.parsePartialToolArgs(ev.item.arguments)
                  const preparationStatus = this.getToolPreparationDescription(ev.item.name, partialArgs)
                  const preparationStatusKey = ev.item.id || ev.item.call_id || ''
                  preparationStatuses[preparationStatusKey] = preparationStatus

                  // first notify
                  yield {
                    type: 'tool',
                    id: ev.item.id,
                    name: ev.item.name,
                    state: 'preparing',
                    status: preparationStatus,
                    done: false
                  }

                  // done
                  break
                }
              }
              break

            case 'response.output_item.done':
              switch (ev.item.type) {
                case 'reasoning':
                  thinking = false
                  break
                case 'function_call': {
                  const toolCall = findPendingCall(ev.item) || ev.item
                  if (!pendingCalls.includes(toolCall)) {
                    pendingCalls.push(toolCall)
                  }
                  mergeToolCall(toolCall, ev.item)
                  startToolExecution(toolCall)
                  break
                }
              }
              break

            case 'response.output_text.delta': {
              if (ev.delta) {
                yield {
                  type: thinking ? 'reasoning' : 'content',
                  text: ev.delta,
                  done: false
                }
              }
              break
            }

            case 'response.function_call_arguments.delta': {
              const call = pendingCalls.find(c => c.id == ev.item_id)
              if (call) {
                call.arguments += ev.delta
                const partialArgs = this.parsePartialToolArgs(call.arguments)
                if (partialArgs !== undefined) {
                  const status = this.getToolPreparationDescription(call.name, partialArgs)
                  const preparationStatusKey = call.id || call.call_id || ''
                  if (status !== preparationStatuses[preparationStatusKey]) {
                    preparationStatuses[preparationStatusKey] = status
                    yield {
                      type: 'tool',
                      id: call.id,
                      name: call.name,
                      state: 'preparing',
                      status,
                      done: false
                    }
                  }
                }
              }
              break
            }

            case 'response.function_call_arguments.done': {
              const call = pendingCalls.find(c => c.id == ev.item_id)
              if (call) {
                if (typeof ev.arguments === 'string') {
                  call.arguments = ev.arguments
                }
                call.name = ev.name || call.name
              }
              break
            }

          }

          yield* drainToolChunks()

        }

        // if no pending calls we are done
        if (!pendingCalls.length) {
          if (opts?.usage) {
            yield { type: 'usage', usage: usage }
          }
          yield { type: 'content', text: '', done: true }
          break
        }

        // Some providers/tests may omit response.output_item.done for function calls.
        // Preserve the old behavior by starting any remaining tools after the stream ends.
        for (const toolCall of pendingCalls) {
          startToolExecution(toolCall)
          yield* drainToolChunks()
        }

        // collect tool call results
        for (const toolCall of pendingCalls) {

          const execution = toolExecutions.get(getToolCallKey(toolCall))
          if (!execution) {
            continue
          }

          const result = yield* waitForToolExecution(execution)
          if (result.error) {
            throw result.error
          }
          if (result.canceled) {
            return  // Stop processing remaining tools
          }

        }

        await this.callHook('beforeToolCallsResponse', context)

        const followReqInput = context.toolHistory
          .filter((entry) => entry.round === context.currentRound)
          .map((entry) => ({
            type: 'function_call_output' as const,
            call_id: entry.id,
            output: typeof entry.result === 'string' ? entry.result : JSON.stringify(entry.result),
          }))

        // now we can build the follow-up request
        const followReq: ResponseCreateParams = {
          model: model.id,
          previous_response_id: responseId,
          input: followReqInput,
          stream: true,
        }
        await this.attachResponsesTools(followReq, model, opts)
        
        // debug
        logger.debug('[responsesStream] FOLLOW-UP STREAM REQ', JSON.stringify(followReq, null, 2))

        // switch stream
        currentStream = await this.client.responses.create(followReq) as AsyncIterable<ResponseStreamEvent>

      }

    }

    return {
      stream: (generator.bind(this))(),
      context
    }
  }

  private async buildResponsesRequestFromMessages(model: ChatModel, thread: Message[], opts: LlmCompletionOpts | undefined, stream: boolean): Promise<ResponseCreateParams> {
    return this.buildResponsesRequest(model, this.buildPayload(model, thread, opts), opts, stream)
  }

  private async buildResponsesRequest(model: ChatModel, payload: LlmCompletionPayload[], opts: LlmCompletionOpts | undefined, stream: boolean): Promise<ResponseCreateParams> {
    
    // helper to extract text from messages
    function extractText(msg: any): string {
      const c = msg?.content
      if (typeof c === 'string') return c
      if (Array.isArray(c)) {
        return c.map((p: any) => typeof p === 'string' ? p : (p.text ?? '')).join('')
      }
      if (c && typeof c === 'object' && typeof c.text === 'string') return c.text
      return JSON.stringify(c)
    }

    function getToolCallId(toolCall: any): string {
      return toolCall?.id || toolCall?.call_id || ''
    }

    function getToolCallName(toolCall: any): string {
      return toolCall?.function?.name || toolCall?.name || ''
    }

    function getToolCallArguments(toolCall: any): string {
      const args = toolCall?.function?.arguments ?? toolCall?.arguments ?? toolCall?.args
      if (typeof args === 'string') return args
      return JSON.stringify(args ?? {})
    }

    // rebuild the instructions
    const instructions = payload
      .filter((m: any) => m.role === 'system')
      .map(extractText)
      .join('\n')
      .trim()

    // now we need to build the input
    let input: string | ResponseInputItem[] = ''

    // if we have a response id we will continue the response
    if (opts?.responseId) {
      input = extractText(payload[payload.length - 1])
    } else {
      input = []
      for (const msg of payload) {
        if (msg.role === 'user') {
          input.push({
            type: 'message',
            role: 'user',
            content: typeof msg.content === 'string' ?
              msg.content :
              msg.content.map((c: LlmContentPayload) => {
                if (c.type === 'text') {
                  return {
                    type: 'input_text',
                    text: c.text
                  }
                } else if (c.type === 'image_url') {
                  return {
                    type: 'input_image',
                    detail: 'auto',
                    image_url: (c as LLmContentPayloadImageOpenai).image_url.url,
                  }
                } else if (c.type === 'image') {
                  return {
                    type: 'input_image',
                    detail: 'auto',
                    image_url: c.source?.data,
                  }
                } else if (c.type === 'document') {
                  return {
                    type: 'input_file',
                    file_data: c.source?.data,
                  }
                } else {
                  // @ts-expect-error unknown content type
                  console.log(`[openai] unknown content type ${c.type} in user message, using JSON.stringify`)
                  return {
                    type: 'input_text',
                    text: JSON.stringify(c)
                  }
                }
              }),

          })

        } else if (msg.role === 'assistant') {

          const toolCalls = Array.isArray(msg.tool_calls) ? msg.tool_calls : []
          const assistantText = extractText(msg)

          if (assistantText || !toolCalls.length) {
            input.push({
              role: 'assistant',
              content: assistantText
            })
          }

          for (const toolCall of toolCalls) {
            input.push({
              type: 'function_call',
              call_id: getToolCallId(toolCall),
              name: getToolCallName(toolCall),
              arguments: getToolCallArguments(toolCall),
            })
          }

        // } else if (msg.role === 'assistant' && msg.messageId?.length) {

        //   // dummy reasoning content
        //   input.push({
        //     type: 'reasoning',
        //     id: msg.messageId.replace('resp_', 'rs_'),
        //     summary: [],
        //   })

        //   // the message itself
        //   input.push({
        //     type: 'message',
        //     role: 'assistant',
        //     status: 'completed',
        //     id: msg.messageId.replace('resp_', 'msg_'),
        //     content: [{
        //       type: 'output_text',
        //       text: extractText(msg),
        //       annotations: [],
        //     }]
        //   })

        } else if (msg.role === 'tool') {
          input.push({
            type: 'function_call_output',
            call_id: msg.tool_call_id || '',
            output: msg.content || '',
          })
        }
      }
    }

    const req: ResponseCreateParams = {
      model: model.id,
      ...(instructions ? { instructions } : {}),
      ...(opts?.responseId ? { previous_response_id: opts.responseId } : {}),
      ...(this.modelSupportsReasoningEffort(model) && opts?.reasoningEffort
        ? { reasoning: { effort: opts.reasoningEffort } }
        : {}),
      input,
      stream,
    // The installed OpenAI SDK type may lag the Responses API's supported
    // reasoning efforts (notably `xhigh`), while the wire format accepts it.
    } as ResponseCreateParams

    await this.attachResponsesTools(req, model, opts)

    // done
    return req
  }

  private async attachResponsesTools(req: ResponseCreateParams, model: ChatModel, opts?: LlmCompletionOpts): Promise<void> {
    const tools = await this.getResponsesTools(model, opts)
    if (!tools.length) {
      return
    }

    req.tools = tools
    req.tool_choice = this.getResponsesToolChoice(opts?.toolChoice)
  }

  async getResponsesTools(model: ChatModel, opts?: LlmCompletionOpts): Promise<Tool[]> {

    // check if enabled
    if (opts?.tools === false || !model.capabilities?.tools) {
      return []
    }

    const internalTools = this.getOpenAIInternalTools(opts)

    // tools - PluginTool[] format
    const toolDefs = await this.getAvailableTools(opts?.toolExecutionDelegate)
    if (!toolDefs.length) return internalTools

    // convert PluginTool[] to Responses API Tool format
    return [
      ...internalTools,
      ...toolDefs.map((tool): Tool => {

        // Build properties object from PluginParameter[]
        const properties: Record<string, any> = {}
        let strict = true

        for (const param of tool.parameters) {
          const converted = this.pluginParamToResponsesSchema(param)
          properties[param.name] = converted.schema
          strict = strict && converted.strict
        }

        // Build parameters schema with strict mode requirements
        // strict mode: required must list ALL property keys
        const parameters = tool.parameters.length > 0 ? {
          type: 'object',
          properties,
          required: Object.keys(properties),
          additionalProperties: false,
        } : {
          type: 'object',
          properties: {},
          required: [],
          additionalProperties: false,
        }

        return {
          type: 'function',
          name: tool.name,
          description: tool.description,
          parameters: parameters,
          strict
        }
      })
    ]

  }

  private getOpenAIInternalTools(opts?: LlmCompletionOpts): Tool[] {
    return this.getInternalTools(this.getId(), opts).map((internalTool) => {
      if ('tool' in internalTool) {
        return internalTool.tool as Tool
      }

      if (internalTool.type === 'web_search') {
        const tool: Record<string, any> = {
          type: internalTool.version || 'web_search',
        }
        if (internalTool.searchContextSize) tool.search_context_size = internalTool.searchContextSize
        if (internalTool.userLocation) tool.user_location = internalTool.userLocation
        if (internalTool.allowedDomains) {
          tool.filters = {
            allowed_domains: internalTool.allowedDomains,
          }
        }
        return tool as Tool
      }

      return internalTool as never
    })
  }

  // Convert a single PluginParameter to Responses API strict mode schema
  private pluginParamToResponsesSchema(param: any): ResponsesSchemaConversion {
    const type = param.type || (param.items ? 'array' : 'string')
    const schema: any = {
      type,
      ...(param.description ? { description: param.description } : {}),
      ...(param.enum ? { enum: param.enum } : {}),
    }

    const passthroughKeys = [
      'properties', 'additionalProperties', 'anyOf', 'oneOf', 'allOf',
      '$defs', 'definitions', 'format', 'minimum', 'maximum', 'minLength',
      'maxLength', 'minItems', 'maxItems',
    ]

    for (const key of passthroughKeys) {
      if (param[key] !== undefined) {
        schema[key] = param[key]
      }
    }

    if (type === 'array' && param.items) {
      schema.items = param.items
    }

    return this.normalizeSchemaForResponses(schema, param.required === false)
  }

  // Helper to convert items for Responses API strict mode
  // Responses API strict mode: required must list ALL property keys
  private normalizeSchemaForResponses(schema: any, nullable = false): ResponsesSchemaConversion {
    if (!schema || typeof schema !== 'object' || Array.isArray(schema)) {
      return this.normalizeSchemaForResponses({ type: 'string' }, nullable)
    }

    const normalized = { ...schema }
    let strict = true

    if (Array.isArray(normalized.type) && normalized.type.some((entry: any) => typeof entry === 'object')) {
      const variants = normalized.type.map((entry: any) => typeof entry === 'string' ? { type: entry } : entry)
      delete normalized.type
      normalized.anyOf = [
        ...(Array.isArray(normalized.anyOf) ? normalized.anyOf : []),
        ...variants,
      ]
    }

    const objectLike = this.schemaHasType(normalized, 'object') || normalized.properties !== undefined
    if (objectLike) {
      if (!normalized.type && !normalized.anyOf && !normalized.oneOf) {
        normalized.type = 'object'
      }

      if (Array.isArray(normalized.properties)) {
        const properties: Record<string, any> = {}
        for (const prop of normalized.properties) {
          const converted = this.pluginParamToResponsesSchema(prop)
          properties[prop.name] = converted.schema
          strict = strict && converted.strict
        }
        normalized.properties = properties
      } else if (normalized.properties && typeof normalized.properties === 'object') {
        const originalRequired = new Set(Array.isArray(normalized.required) ? normalized.required : [])
        const hasOriginalRequired = Array.isArray(normalized.required)
        const properties: Record<string, any> = {}

        for (const [key, value] of Object.entries(normalized.properties)) {
          const converted = this.normalizeSchemaForResponses(value, hasOriginalRequired && !originalRequired.has(key))
          properties[key] = converted.schema
          strict = strict && converted.strict
        }

        normalized.properties = properties
      } else {
        normalized.properties = {}
      }

      normalized.required = Object.keys(normalized.properties)

      if (normalized.additionalProperties === undefined || normalized.additionalProperties === false) {
        normalized.additionalProperties = false
      } else {
        strict = false
      }
    }

    if (this.schemaHasType(normalized, 'array') || normalized.items !== undefined) {
      const convertedItems = this.normalizeSchemaForResponses(normalized.items || { type: 'string' })
      normalized.items = convertedItems.schema
      strict = strict && convertedItems.strict
    }

    for (const key of ['anyOf', 'oneOf', 'allOf']) {
      if (Array.isArray(normalized[key])) {
        normalized[key] = normalized[key].map((entry: any) => {
          const converted = this.normalizeSchemaForResponses(entry)
          strict = strict && converted.strict
          return converted.schema
        })
      }
    }

    const result = nullable ? this.makeSchemaNullable(normalized) : normalized
    return { schema: result, strict }
  }

  private schemaHasType(schema: any, type: string): boolean {
    return schema.type === type || (Array.isArray(schema.type) && schema.type.includes(type))
  }

  private makeSchemaNullable(schema: any): any {
    if (schema.enum && !schema.enum.includes(null)) {
      schema.enum = [...schema.enum, null]
    }

    if (typeof schema.type === 'string') {
      if (schema.type !== 'null') {
        schema.type = [schema.type, 'null']
      }
    } else if (Array.isArray(schema.type)) {
      if (!schema.type.includes('null')) {
        schema.type = [...schema.type, 'null']
      }
    } else if (Array.isArray(schema.anyOf)) {
      if (!schema.anyOf.some((entry: any) => entry?.type === 'null')) {
        schema.anyOf = [...schema.anyOf, { type: 'null' }]
      }
    } else {
      schema.anyOf = [{ ...schema }, { type: 'null' }]
    }

    return schema
  }

  private getResponsesToolChoice(toolChoice?: LlmToolChoice): ToolChoiceOptions|ToolChoiceFunction {

    if (!toolChoice) {
      return 'auto'
    }

    switch (toolChoice.type) {
      case 'none':
        return 'none'
      case 'auto':
        return 'auto'
      case 'required':
        return 'required'
      case 'tool':
        return {
          type: 'function',
          name: toolChoice.name
        }
    }

  }

  async continueResponse(model: ChatModel, previousId: string, input: string, opts?: LlmCompletionOpts): Promise<LlmResponse> {
    const req: any = {
      model: model.id,
      input: [{ type: 'message', role: 'user', content: input }],
      previous_response_id: previousId,
      stream: false,
    }
    const toolOpts = await this.getToolsOpts(model, opts)
    if ((toolOpts as any).tools) req.tools = (toolOpts as any).tools

    const resp: any = await (this.client as any).responses.create(req)
    return {
      type: 'text',
      content: (resp.output?.text) ?? '',
      ...(opts?.usage && resp.usage ? { usage: resp.usage } : {}),
    }
  }

  async forkResponse(model: ChatModel, previousId: string, input: string, opts?: LlmCompletionOpts): Promise<LlmResponse> {
    // For fork we simply call continueResponse for now (no server-side diff yet)
    return await this.continueResponse(model, previousId, input, opts)
  }

  private accumulateResponsesUsage(cumulate: LlmUsage, usage: ResponseUsage) {
    cumulate.prompt_tokens += usage.input_tokens
    cumulate.completion_tokens += usage.output_tokens
    cumulate.prompt_tokens_details!.cached_tokens! += usage.input_tokens_details.cached_tokens
    cumulate.completion_tokens_details!.reasoning_tokens! += usage.output_tokens_details.reasoning_tokens
  }

}
